import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import axios from 'axios';

// Components
import Preloader from './components/Preloader';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Public Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import WorkflowsPage from './pages/WorkflowsPage';
import PortfolioPage from './pages/PortfolioPage';
import GetProposalPage from './pages/GetProposalPage';
import BookCallPage from './pages/BookCallPage';
import DemoPortalPage from './pages/DemoPortalPage';
import LoginPage from './pages/LoginPage';
import AboutPage from './pages/AboutPage';

// Admin Pages
import LeadsPage from './pages/admin/LeadsPage';
import ClientsPage from './pages/admin/ClientsPage';
import ProjectsPage from './pages/admin/ProjectsPage';
import DeliverablesPage from './pages/admin/DeliverablesPage';
import AdminApprovalsPage from './pages/admin/ApprovalsPage';
import AdminCalendarPage from './pages/admin/CalendarPage';
import AdminReportsPage from './pages/admin/ReportsPage';
import AdminSettingsPage from './pages/admin/SettingsPage';
import AdminRequestsPage from './pages/admin/RequestsPage';
import AdminServicesPage from './pages/admin/ServicesPage';
import AdminUsersPage from './pages/admin/UsersPage';

// Client Pages
import ClientDashboard from './pages/client/DashboardPage';
import ClientApprovalsPage from './pages/client/ApprovalsPage';
import ClientCalendarPage from './pages/client/CalendarPage';
import ClientUploadsPage from './pages/client/UploadsPage';
import ClientReportsPage from './pages/client/ReportsPage';
import ClientRequestsPage from './pages/client/RequestsPage';

import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-fp-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    if (user?.role === 'client') {
      return <Navigate to="/client/dashboard" replace />;
    }
    return <Navigate to="/admin/leads" replace />;
  }

  return children;
};

// Public Layout Component
const PublicLayout = ({ children, showNav = true, showFooter = true }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-fp-bg">
      {/* Film Grain Overlay - Very Subtle */}
      {settings?.enable_grain !== false && <div className="film-grain" />}
      
      {showNav && <Navbar />}
      <main>{children}</main>
      {showFooter && <Footer />}
    </div>
  );
};

// Main App Content
const AppContent = () => {
  const [showPreloader, setShowPreloader] = useState(true);

  // No auto-seeding - database is initialized manually or via admin

  if (showPreloader) {
    return <Preloader onComplete={() => setShowPreloader(false)} />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <PublicLayout>
              <HomePage />
            </PublicLayout>
          }
        />
        <Route
          path="/services"
          element={
            <PublicLayout>
              <ServicesPage />
            </PublicLayout>
          }
        />
        <Route
          path="/workflows"
          element={
            <PublicLayout>
              <WorkflowsPage />
            </PublicLayout>
          }
        />
        <Route
          path="/portfolio"
          element={
            <PublicLayout>
              <PortfolioPage />
            </PublicLayout>
          }
        />
        <Route
          path="/about"
          element={
            <PublicLayout>
              <AboutPage />
            </PublicLayout>
          }
        />
        <Route
          path="/get-proposal"
          element={
            <PublicLayout>
              <GetProposalPage />
            </PublicLayout>
          }
        />
        <Route
          path="/book-call"
          element={
            <PublicLayout>
              <BookCallPage />
            </PublicLayout>
          }
        />
        <Route
          path="/demo"
          element={
            <PublicLayout>
              <DemoPortalPage />
            </PublicLayout>
          }
        />
        <Route
          path="/login"
          element={
            <PublicLayout showNav={false} showFooter={false}>
              <LoginPage />
            </PublicLayout>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <AdminRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/services"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdminServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <LeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/projects"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/deliverables"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <DeliverablesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/approvals"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <AdminApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/calendar"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <AdminCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute allowedRoles={['admin', 'team', 'super_admin', 'manager']}>
              <AdminReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />

        {/* Client Routes */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/requests"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/approvals"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/calendar"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/uploads"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientUploadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/reports"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientReportsPage />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0f172a',
            color: '#f8fafc',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      />
    </BrowserRouter>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
